import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { Search, FileText, Download, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const ENTITIES = ['user', 'role', 'participant', 'funding_plan', 'shift', 'timesheet', 'invoice', 'incident', 'staff', 'notification', 'participant_appointment', 'participant_feedback', 'engagement_message', 'announcement', 'participant_contact', 'audit_logs', 'sensitive_fields'];
const ACTIONS = ['LOGIN', 'LOGOUT', 'CREATE', 'UPDATE', 'APPROVE', 'REJECT', 'SUBMIT', 'ASSIGN', 'CHECK_IN', 'CHECK_OUT', 'EXPORT', 'SEND', 'ARCHIVE', 'DEACTIVATE', 'CLOSE', 'CONFIRM', 'COMPLETE', 'CANCEL', 'RUN', 'ENCRYPT', 'BULK_ENCRYPT', 'DECRYPT_VIEW', 'TRIGGER'];

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState('all');
  const [action, setAction] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20', sortBy: 'timestamp', sortDir: 'desc' });
      if (entity !== 'all') params.set('entity', entity);
      if (action !== 'all') params.set('action', action);
      const res = await api.get(`/audit?${params.toString()}`);
      setLogs(res.data.items || res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) { toast.error('Failed to load audit logs'); }
  }, [page, entity, action]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = logs.filter(l => {
    if (!search) return true;
    return l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
           l.entity_id?.includes(search) || l.action?.toLowerCase().includes(search.toLowerCase()) ||
           l.user_id?.includes(search);
  });

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({ format, pageSize: '5000' });
      if (entity !== 'all') params.set('entityType', entity);
      if (action !== 'all') params.set('actionType', action);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);

      const res = await api.get(`/security/audit/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_export.${format.toLowerCase() === 'json' ? 'json' : 'csv'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format}`);
    } catch { toast.error('Export failed'); }
  };

  const loadSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await api.get(`/security/audit/summary?${params}`);
      setSummary(res.data.data);
      setShowSummary(true);
    } catch { toast.error('Failed to load summary'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight">Audit Logs</h1><p className="text-muted-foreground mt-1">System activity trail</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadSummary} data-testid="summary-btn"><BarChart3 className="w-4 h-4 mr-1" />Summary</Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('CSV')} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('JSON')} data-testid="export-json-btn"><Download className="w-4 h-4 mr-1" />JSON</Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-end md:flex-wrap">
        <div className="relative flex-1 md:w-[240px] md:flex-none">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search user, action, entity..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="audit-search" />
        </div>
        <Select value={entity} onValueChange={(v) => { setEntity(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="entity-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={(v) => { setAction(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2 items-end">
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36 h-9" data-testid="date-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36 h-9" data-testid="date-to" /></div>
        </div>
        <span className="text-sm text-muted-foreground">{total} log(s)</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Timestamp</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Entity ID</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No logs</TableCell></TableRow>
              : filtered.map(l => (
                <TableRow key={l.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => setDetail(l)} data-testid={`audit-row-${l.id}`}>
                  <TableCell className="font-mono text-xs tabular-nums">{l.timestamp?.replace('T', ' ').slice(0, 19)}</TableCell>
                  <TableCell className="text-sm">{l.user_name || l.user_id?.slice(0, 8)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{l.action}</Badge></TableCell>
                  <TableCell className="text-xs">{l.entity}</TableCell>
                  <TableCell className="font-mono text-xs">{l.entity_id?.slice(0, 12)}{l.entity_id?.length > 12 ? '...' : ''}</TableCell>
                  <TableCell><Button variant="ghost" size="sm"><FileText className="h-3 w-3" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationFooter page={page} pageSize={20} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Audit Log Detail</DialogTitle><DialogDescription>{detail?.action} on {detail?.entity}</DialogDescription></DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-muted-foreground">Timestamp:</span> <span className="font-mono">{detail?.timestamp}</span></div>
              <div><span className="text-muted-foreground">User:</span> {detail?.user_name || detail?.user_id}</div>
              <div><span className="text-muted-foreground">Action:</span> <Badge>{detail?.action}</Badge></div>
              <div><span className="text-muted-foreground">Entity:</span> {detail?.entity} / <span className="font-mono text-xs">{detail?.entity_id}</span></div>
            </div>
            {detail?.before && (<div><p className="font-medium mb-1">Before:</p><pre className="bg-muted rounded p-3 text-xs overflow-x-auto max-h-40">{JSON.stringify(detail.before, null, 2)}</pre></div>)}
            {detail?.after && (<div><p className="font-medium mb-1">After:</p><pre className="bg-muted rounded p-3 text-xs overflow-x-auto max-h-40">{JSON.stringify(detail.after, null, 2)}</pre></div>)}
            {detail?.meta && Object.keys(detail.meta).length > 0 && (<div><p className="font-medium mb-1">Meta:</p><pre className="bg-muted rounded p-3 text-xs overflow-x-auto max-h-40">{JSON.stringify(detail.meta, null, 2)}</pre></div>)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Summary Dialog */}
      <Dialog open={showSummary} onOpenChange={setShowSummary}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Audit Log Summary</DialogTitle></DialogHeader>
          {summary && (
            <div className="space-y-4">
              <p className="text-2xl font-bold">{summary.totalLogs} <span className="text-base font-normal text-muted-foreground">total logs</span></p>
              <div>
                <p className="text-sm font-medium mb-2">Top Actions</p>
                <div className="space-y-1">
                  {summary.byAction?.slice(0, 8).map(a => (
                    <div key={a.action} className="flex justify-between text-sm"><span>{a.action}</span><Badge variant="outline">{a.count}</Badge></div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Top Entities</p>
                <div className="space-y-1">
                  {summary.byEntity?.slice(0, 8).map(e => (
                    <div key={e.entity} className="flex justify-between text-sm"><span>{e.entity}</span><Badge variant="outline">{e.count}</Badge></div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
